import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { format } from 'date-fns';

const TIPOS_CIRURGIA_PREDEFINIDOS = [
  'ALIF', 'OLIF', 'TLIF', 'PLIF', 'Posterolateral',
  'Cervical Anterior Cage Bloqueado', 'Cervical Anterior Placa',
  'Cervical Posterior', 'Escoliose', 'Descompressão Isolada'
];

function TiposCirurgiaEditor({ value, onChange }) {
  const [extraInput, setExtraInput] = useState('');
  const extras = value.filter(t => !TIPOS_CIRURGIA_PREDEFINIDOS.includes(t));

  function togglePredefinido(tipo) {
    onChange(value.includes(tipo) ? value.filter(t => t !== tipo) : [...value, tipo]);
  }

  function addExtra() {
    const val = extraInput.trim();
    if (val && !value.includes(val)) onChange([...value, val]);
    setExtraInput('');
  }

  return (
    <div className="form-group">
      <label className="form-label">Tipos de cirurgia permitidos</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        {TIPOS_CIRURGIA_PREDEFINIDOS.map(t => (
          <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', border: `1px solid ${value.includes(t) ? 'var(--blue)' : 'var(--border)'}`, borderRadius: 8, cursor: 'pointer', background: value.includes(t) ? '#eff6ff' : 'var(--surface)', fontSize: 13, transition: 'all .15s' }}>
            <input type="checkbox" checked={value.includes(t)} onChange={() => togglePredefinido(t)} style={{ accentColor: 'var(--blue)' }} />
            {t}
          </label>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: extras.length > 0 ? 8 : 0 }}>
        <input className="form-input" value={extraInput} onChange={e => setExtraInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addExtra())} placeholder="Tipo adicional..." style={{ flex: 1 }} />
        <button className="btn btn-secondary" type="button" onClick={addExtra} style={{ whiteSpace: 'nowrap' }}>Adicionar</button>
      </div>
      {extras.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {extras.map(e => (
            <span key={e} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', background: '#f0f9ff', border: '1px solid var(--blue)', borderRadius: 20, fontSize: 13 }}>
              {e}
              <button onClick={() => onChange(value.filter(t => t !== e))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0, color: 'var(--text-2)' }}>×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function NovaPesquisaModal({ onClose, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState({ titulo: '', descricao: '' });
  const [tiposCirurgia, setTiposCirurgia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSave() {
    if (!form.titulo) { setError('Informe o título da pesquisa.'); return; }
    setLoading(true);
    const { error } = await supabase.from('pesquisas').insert({
      ...form,
      tipos_cirurgia: tiposCirurgia.length > 0 ? tiposCirurgia : null,
      criado_por: user.id
    });
    setLoading(false);
    if (error) { setError(error.message); return; }
    onSaved();
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3>Nova pesquisa</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">⚠ {error}</div>}
          <div className="form-group">
            <label className="form-label">Título da pesquisa *</label>
            <input className="form-input" value={form.titulo} onChange={e => setForm(f => ({...f, titulo: e.target.value}))} placeholder="Ex: Estudo comparativo de artrodese lombar" autoFocus />
          </div>
          <div className="form-group">
            <label className="form-label">Descrição</label>
            <textarea className="form-textarea" value={form.descricao} onChange={e => setForm(f => ({...f, descricao: e.target.value}))} placeholder="Objetivos, metodologia, critérios de inclusão..." />
          </div>
          <TiposCirurgiaEditor value={tiposCirurgia} onChange={setTiposCirurgia} />
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
            {loading ? <><span className="spinner" />Criando...</> : 'Criar pesquisa'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Calcula completude de uma pesquisa
async function calcularCompletude(pesquisaId) {
  const [ppRes, momRes, scoresRes, paramRes] = await Promise.all([
    supabase.from('pesquisa_pacientes').select('id').eq('pesquisa_id', pesquisaId),
    supabase.from('pesquisa_momentos').select('id, tipo_momento').eq('pesquisa_id', pesquisaId),
    supabase.from('pesquisa_scores').select('score_id').eq('pesquisa_id', pesquisaId),
    supabase.from('pesquisa_parametros_radio').select('*').eq('pesquisa_id', pesquisaId).maybeSingle(),
  ]);

  const ppIds = (ppRes.data || []).map(p => p.id);
  if (!ppIds.length) return { completos: 0, emPreenchimento: 0 };

  const momentos = momRes.data || [];
  const momentosScore = momentos.filter(m => m.tipo_momento === 'score' || m.tipo_momento === 'ambos');
  const momentosRadio = momentos.filter(m => m.tipo_momento === 'radio' || m.tipo_momento === 'ambos');
  const scoreIds = (scoresRes.data || []).map(s => s.score_id);
  const param = paramRes.data;
  const temRadio = param && Object.entries(param).some(([k, v]) =>
    !['id','pesquisa_id','extras','criado_em','atualizado_em'].includes(k) && v === true
  );

  const [avalRes, radioRes, cirRes] = await Promise.all([
    supabase.from('avaliacoes').select('pesquisa_paciente_id, momento_id, score_id, status')
      .in('pesquisa_paciente_id', ppIds).eq('status', 'assinada'),
    temRadio ? supabase.from('avaliacoes_radio').select('pesquisa_paciente_id, momento_id')
      .in('pesquisa_paciente_id', ppIds) : Promise.resolve({ data: [] }),
    supabase.from('cirurgias').select('pesquisa_paciente_id').in('pesquisa_paciente_id', ppIds),
  ]);

  const avals = avalRes.data || [];
  const radios = radioRes.data || [];
  const cirIds = new Set((cirRes.data || []).map(c => c.pesquisa_paciente_id));

  let completos = 0, emPreenchimento = 0;

  for (const ppId of ppIds) {
    // Sem cirurgia = em preenchimento
    if (!cirIds.has(ppId)) { emPreenchimento++; continue; }

    let completo = true;

    // Verificar todos os scores em todos os momentos de score
    for (const mom of momentosScore) {
      for (const scoreId of scoreIds) {
        const tem = avals.some(a => a.pesquisa_paciente_id === ppId && a.momento_id === mom.id && a.score_id === scoreId);
        if (!tem) { completo = false; break; }
      }
      if (!completo) break;
    }

    // Verificar radiografias em todos os momentos de radio
    if (completo && temRadio) {
      for (const mom of momentosRadio) {
        const tem = radios.some(r => r.pesquisa_paciente_id === ppId && r.momento_id === mom.id);
        if (!tem) { completo = false; break; }
      }
    }

    if (completo) completos++; else emPreenchimento++;
  }

  return { completos, emPreenchimento };
}

const TIPO_LABEL = { score: 'Score', radio: 'Radiografia', ambos: 'Score + Radio', tc: 'TC', rm: 'RM' };

function offsetLabel(dias) {
  if (dias === null) return 'Pré-operatório';
  if (dias === 90) return '3 meses';
  if (dias === 180) return '6 meses';
  if (dias === 365) return '1 ano';
  return `${dias} dias`;
}

export default function PesquisasPage() {
  const { isSuperAdmin, isAdmin } = useAuth();
  const [pesquisas, setPesquisas] = useState([]);
  const [completude, setCompletude] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalPesquisa, setModalPesquisa] = useState(null);
  const [modalScores, setModalScores] = useState([]);
  const [modalMomentos, setModalMomentos] = useState([]);
  const [modalEditando, setModalEditando] = useState(false);
  const [modalDescricao, setModalDescricao] = useState('');
  const [modalSalvando, setModalSalvando] = useState(false);

  async function load() {
    setLoading(true);
    const { data } = await supabase.from('pesquisas').select('*, profiles!criado_por(nome_completo)').order('criado_em', { ascending: false });
    setPesquisas(data || []);
    setLoading(false);

    // Calcular completude para cada pesquisa em paralelo
    const resultados = await Promise.all((data || []).map(async p => {
      const r = await calcularCompletude(p.id);
      return [p.id, r];
    }));
    setCompletude(Object.fromEntries(resultados));
  }

  useEffect(() => { load(); }, []);

  async function abrirModalDescricao(pesquisa) {
    setModalPesquisa(pesquisa);
    setModalDescricao(pesquisa.descricao || '');
    setModalEditando(false);
    setModalScores([]);
    setModalMomentos([]);
    const [scoresRes, momRes] = await Promise.all([
      supabase.from('pesquisa_scores').select('score_id, scores(nome)').eq('pesquisa_id', pesquisa.id),
      supabase.from('pesquisa_momentos').select('nome, tipo_momento, dias_offset').eq('pesquisa_id', pesquisa.id).order('dias_offset', { ascending: true, nullsFirst: true }),
    ]);
    setModalScores(scoresRes.data || []);
    setModalMomentos(momRes.data || []);
  }

  async function salvarDescricao() {
    setModalSalvando(true);
    await supabase.from('pesquisas').update({ descricao: modalDescricao }).eq('id', modalPesquisa.id);
    setPesquisas(ps => ps.map(p => p.id === modalPesquisa.id ? { ...p, descricao: modalDescricao } : p));
    setModalPesquisa(mp => ({ ...mp, descricao: modalDescricao }));
    setModalEditando(false);
    setModalSalvando(false);
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Pesquisas</h1>
          <p>Protocolos ativos e encerrados</p>
        </div>
        {isAdmin && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Nova pesquisa</button>
        )}
      </div>

      {loading ? (
        <div className="card"><div style={{ padding: 32, textAlign: 'center' }} className="text-muted text-sm">Carregando...</div></div>
      ) : pesquisas.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-icon">🔬</div>
            <h3>Nenhuma pesquisa cadastrada</h3>
            <p>Crie a primeira pesquisa clínica.</p>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {pesquisas.map(p => {
            const c = completude[p.id];
            return (
              <div key={p.id} className="card">
                <div className="card-body">
                  <div className="flex-between" style={{ alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div className="flex-row" style={{ marginBottom: 6, flexWrap: 'wrap', gap: 8 }}>
                        <strong style={{ fontSize: 16 }}>{p.titulo}</strong>
                        <span className={`badge ${p.ativa ? 'badge-teal' : 'badge-gray'}`}>{p.ativa ? 'Ativa' : 'Encerrada'}</span>
                      </div>
                      {p.descricao && <p className="text-muted text-sm" style={{ maxWidth: 600, marginBottom: 8 }}>{p.descricao}</p>}

                      {/* Contadores de completude */}
                      <div className="flex-row" style={{ gap: 16, marginBottom: 6 }}>
                        {c ? (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#f59e0b', flexShrink: 0 }} />
                              <span className="text-sm"><strong>{c.emPreenchimento}</strong> em preenchimento</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#16a34a', flexShrink: 0 }} />
                              <span className="text-sm"><strong>{c.completos}</strong> completos</span>
                            </div>
                          </>
                        ) : (
                          <span className="text-sm text-muted">Calculando...</span>
                        )}
                      </div>

                      <p className="text-sm text-muted">
                        Criada por {p.profiles?.nome_completo || '—'} · {format(new Date(p.criado_em), 'dd/MM/yyyy')}
                      </p>
                    </div>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={e => { e.stopPropagation(); abrirModalDescricao(p); }}
                      title="Ver descrição da pesquisa"
                      style={{ flexShrink: 0 }}
                    >
                      📋
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && <NovaPesquisaModal onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); load(); }} />}

      {modalPesquisa && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModalPesquisa(null)}>
          <div className="modal" style={{ maxWidth: 600, maxHeight: '85vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <div>
                <h3 style={{ margin: '0 0 4px' }}>{modalPesquisa.titulo}</h3>
                <span className={`badge ${modalPesquisa.ativa ? 'badge-teal' : 'badge-gray'}`}>
                  {modalPesquisa.ativa ? 'Ativa' : 'Encerrada'}
                </span>
              </div>
              <button className="btn-close" onClick={() => setModalPesquisa(null)}>×</button>
            </div>
            <div className="modal-body">

              {/* Descrição */}
              <div style={{ marginBottom: 4 }}>
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '.06em', marginBottom: 8 }}>
                  Descrição
                </div>
                {modalEditando ? (
                  <>
                    <textarea
                      className="form-textarea"
                      value={modalDescricao}
                      onChange={e => setModalDescricao(e.target.value)}
                      style={{ width: '100%', minHeight: 120 }}
                    />
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button className="btn btn-primary btn-sm" onClick={salvarDescricao} disabled={modalSalvando}>
                        {modalSalvando ? 'Salvando…' : 'Salvar'}
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setModalEditando(false)} disabled={modalSalvando}>
                        Cancelar
                      </button>
                    </div>
                  </>
                ) : (
                  <div>
                    {modalDescricao ? (
                      <p style={{ margin: 0, lineHeight: 1.7 }}>{modalDescricao}</p>
                    ) : (
                      <p style={{ margin: 0, color: 'var(--text-muted)', fontStyle: 'italic' }}>Nenhuma descrição cadastrada.</p>
                    )}
                    {(isSuperAdmin || isAdmin) && (
                      <button className="btn btn-secondary btn-sm" onClick={() => setModalEditando(true)} style={{ marginTop: 10 }}>
                        Editar
                      </button>
                    )}
                  </div>
                )}
              </div>

              <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '20px 0' }} />

              {/* Scores */}
              <div style={{ marginBottom: 4 }}>
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '.06em', marginBottom: 8 }}>
                  Scores configurados
                </div>
                {modalScores.length === 0 ? (
                  <p className="text-sm text-muted" style={{ margin: 0 }}>Nenhum score configurado.</p>
                ) : (
                  <ul style={{ margin: 0, padding: '0 0 0 18px' }}>
                    {modalScores.map((s, i) => (
                      <li key={i} className="text-sm">{s.scores?.nome || s.score_id}</li>
                    ))}
                  </ul>
                )}
              </div>

              <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '20px 0' }} />

              {/* Momentos */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '.06em', marginBottom: 8 }}>
                  Momentos configurados
                </div>
                {modalMomentos.length === 0 ? (
                  <p className="text-sm text-muted" style={{ margin: 0 }}>Nenhum momento configurado.</p>
                ) : (
                  <ul style={{ margin: 0, padding: '0 0 0 18px' }}>
                    {modalMomentos.map((m, i) => (
                      <li key={i} className="text-sm">
                        {m.nome} · {TIPO_LABEL[m.tipo_momento] || m.tipo_momento} · {offsetLabel(m.dias_offset)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModalPesquisa(null)}>Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
